/**
 * Dialer tab — phone number input + caller ID selector + call button.
 * Part of the Call Manager modal.
 */

import { useCallback, useState } from 'react';
import { Phone, PhoneOff, Delete, ChevronDown, Loader2, MessageCircle, Send, Star, EyeOff, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCallStore } from '@/stores/call-store';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useUIStore } from '@/stores/ui-store';
import { usePermissions } from '@/hooks/use-permissions';
import { useVisibleCallerIds } from '@/hooks/use-visible-caller-ids';
import { useConversationStore } from '@/stores/conversation-store';
import { Keypad } from './keypad';

export function Dialer({ large }: { large?: boolean } = {}) {
  const dialerNumber = useCallStore((s) => s.dialerNumber);
  const setDialerNumber = useCallStore((s) => s.setDialerNumber);
  // KAN-178: already filtered by Teams & Permissions (caller-id visibility + hidden senders)
  const callerIds = useVisibleCallerIds();
  const selectedCallerId = useCallStore((s) => s.selectedCallerId);
  const setSelectedCallerId = useCallStore((s) => s.setSelectedCallerId);
  const makeCall = useCallStore((s) => s.makeCall);
  const softphoneState = useCallStore((s) => s.softphoneState);
  const voiceError = useCallStore((s) => s.voiceError);
  const toggleCallManager = useCallStore((s) => s.toggleCallManager);
  const whatsAppCallPermissions = useCallStore((s) => s.whatsAppCallPermissions);
  const requestWhatsAppCallPermission = useCallStore((s) => s.requestWhatsAppCallPermission);
  const { callPermissions } = usePermissions();

  // Detect if selected caller ID is a WhatsApp sender
  const selectedCallerObj = callerIds.find((c) => c.value === selectedCallerId);
  const isWhatsAppCallerId = selectedCallerObj?.channel === 'WHATSAPP';

  // Resolve existing WhatsApp call permission from the conversation doc.
  // The server writes `callPermission: { response, is_permanent, expiration_timestamp }`
  // when a call_permission_reply webhook arrives.
  const conversations = useConversationStore((s) => s.conversations);
  const persistedWaPermission = (() => {
    if (!isWhatsAppCallerId || !dialerNumber.trim()) return null;
    const digits = dialerNumber.replace(/\D/g, '');
    if (!digits) return null;
    // Find conversation where contactPhone matches (digits-only)
    const conv = conversations.find((c) => c.contactPhone?.replace(/\D/g, '') === digits);
    const cp = conv?.metadata?.callPermission as
      | { response?: string; is_permanent?: boolean; expiration_timestamp?: string }
      | undefined;
    if (!cp) return null;
    if (cp.response === 'reject') return 'rejected' as const;
    if (cp.response === 'accept') {
      const expMs = cp.expiration_timestamp ? parseInt(cp.expiration_timestamp, 10) * 1000 : undefined;
      const expired = !cp.is_permanent && expMs ? expMs < Date.now() : false;
      if (!expired) return 'accepted' as const;
    }
    return null;
  })();

  // WhatsApp permission status for the current dialer number.
  // Priority: in-memory state (for fresh requests) > persisted Firestore state > 'none'
  const waPermissionStatus = isWhatsAppCallerId && dialerNumber.trim()
    ? (whatsAppCallPermissions[dialerNumber.trim()] || persistedWaPermission || 'none')
    : null;

  const handleDigit = useCallback(
    (digit: string) => {
      setDialerNumber(dialerNumber + digit);
    },
    [dialerNumber, setDialerNumber]
  );

  const handleBackspace = useCallback(() => {
    setDialerNumber(dialerNumber.slice(0, -1));
  }, [dialerNumber, setDialerNumber]);

  const handleCall = useCallback(async () => {
    if (!dialerNumber.trim()) return;
    try {
      await makeCall(dialerNumber.trim());
      toggleCallManager(); // Close the manager
    } catch (error) {
      console.error('[Dialer] Failed to make call:', error);
    }
  }, [dialerNumber, makeCall, toggleCallManager]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Only allow digits, +, *, # and spaces
      const cleaned = e.target.value.replace(/[^0-9+*#\s()-]/g, '');
      setDialerNumber(cleaned);
    },
    [setDialerNumber]
  );

  // Shared user check
  const user = useAuthStore((s) => s.user);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const isAdmin = !selectedAdminUserId || selectedAdminUserId === user?.uid;

  const isAvailable = useCallStore((s) => s.isAvailable);
  const enableVoiceCalling = useSettingsStore((s) => s.enableVoiceCalling);

  // CRM phone fields — show quick-pick buttons when multiple numbers exist
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);

  const isReady = softphoneState === 'registered';
  const dialerDisabled = !isReady || callerIds.length === 0;
  const canCall = isReady && dialerNumber.trim().length > 0 && !!selectedCallerId && callPermissions.canMakeOutboundCalls;

  return (
    <div data-testid="dialer" className={cn('flex flex-col items-center', large ? 'px-5 py-4' : 'px-4 py-2')}>
      {/* Caller ID selector */}
      <CallerIdPicker
        callerIds={callerIds}
        selectedCallerId={selectedCallerId}
        onSelect={setSelectedCallerId}
      />

      {/* Phone number input */}
      <div className={cn('w-full flex items-center gap-2', large ? 'mb-4' : 'mb-3')}>
        <input
          type="tel"
          data-testid="dialer-input"
          value={dialerNumber}
          onChange={handleInputChange}
          placeholder="Enter phone number"
          disabled={dialerDisabled}
          className={cn(
            'flex-1 bg-transparent text-center font-light border-none outline-none placeholder:text-lg placeholder:font-normal placeholder:tracking-normal',
            large ? 'text-2xl' : 'text-xl',
            dialerDisabled
              ? 'text-muted-foreground/40 placeholder:text-muted-foreground/30 cursor-not-allowed'
              : 'text-foreground placeholder:text-muted-foreground/50'
          )}
          autoFocus={!dialerDisabled}
        />
        {dialerNumber && (
          <button
            onClick={handleBackspace}
            className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Delete"
          >
            <Delete className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* CRM phone quick-pick — shown when CRM record has phone numbers */}
      {crmRecordContext && crmPhoneFields.length > 1 && (
        <div className="w-full flex flex-wrap gap-1.5 mb-2 justify-center">
          {crmPhoneFields.map((field) => {
            const digits = field.value.replace(/\D/g, '');
            const isActive = dialerNumber.replace(/\D/g, '') === digits;
            return (
              <button
                key={field.key || field.apiName}
                type="button"
                onClick={() => setDialerNumber(field.value.trim())}
                disabled={dialerDisabled}
                className={cn(
                  'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border',
                  isActive
                    ? 'bg-primary/10 text-primary border-primary/30'
                    : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted hover:text-foreground'
                )}
              >
                {field.displayLabel}
              </button>
            );
          })}
        </div>
      )}

      {/* Keypad — keyboard disabled because the <input> already handles key presses */}
      <Keypad
        onDigit={handleDigit}
        enableKeyboard={false}
        disabled={dialerDisabled}
        className={cn(large ? 'mb-4' : 'mb-2', 'mx-auto w-fit')}
      />

      {/* Call button — WhatsApp permission flow or direct call */}
      {isWhatsAppCallerId && waPermissionStatus === 'pending' ? (
        <div className="flex flex-col items-center gap-1.5">
          <div className={cn('flex items-center justify-center rounded-full bg-amber-500/15', large ? 'h-14 w-14' : 'h-12 w-12')}>
            <Loader2 className={cn('text-amber-500 animate-spin', large ? 'h-6 w-6' : 'h-5 w-5')} />
          </div>
          <p className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
            Awaiting permission...
          </p>
        </div>
      ) : isWhatsAppCallerId && waPermissionStatus === 'rejected' ? (
        <div className="flex flex-col items-center gap-1.5">
          <div className={cn('flex items-center justify-center rounded-full bg-destructive/15', large ? 'h-14 w-14' : 'h-12 w-12')}>
            <PhoneOff className={cn('text-destructive', large ? 'h-6 w-6' : 'h-5 w-5')} />
          </div>
          <p className="text-[11px] text-destructive font-medium">
            Permission denied
          </p>
        </div>
      ) : isWhatsAppCallerId && waPermissionStatus === 'none' && dialerNumber.trim() ? (
        <div className="flex flex-col items-center gap-1.5">
          <button
            onClick={() => requestWhatsAppCallPermission(dialerNumber.trim())}
            disabled={!isReady || !selectedCallerId || !callPermissions.canUseWhatsAppCalling}
            className={cn(
              'flex items-center justify-center gap-2 rounded-full transition-colors shadow-lg',
            large ? 'h-14 px-6' : 'h-12 px-5',
              isReady && selectedCallerId
                ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            )}
            title="Send call permission request via WhatsApp"
          >
            <Send className="h-5 w-5" />
            <span className="text-sm font-medium">Send Call Request</span>
          </button>
          <p className="text-[10px] text-muted-foreground text-center max-w-[220px]">
            WhatsApp calls require permission from the recipient
          </p>
        </div>
      ) : (
        <button
          data-testid="call-button"
          onClick={handleCall}
          disabled={!canCall}
          className={cn(
            'flex items-center justify-center gap-2 rounded-full transition-colors shadow-lg',
            large
              ? (isWhatsAppCallerId ? 'h-14 px-6' : 'h-14 w-14')
              : (isWhatsAppCallerId ? 'h-12 px-5' : 'h-12 w-12'),
            canCall
              ? isWhatsAppCallerId
                ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                : 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          )}
          title={!isReady ? 'Softphone not ready' : isWhatsAppCallerId ? 'WhatsApp Call' : 'Call'}
        >
          {isWhatsAppCallerId ? (
            <>
              <MessageCircle className={cn(large ? 'h-5 w-5' : 'h-4 w-4')} />
              <span className="text-sm font-medium">WhatsApp Call</span>
            </>
          ) : (
            <Phone className={cn(large ? 'h-6 w-6' : 'h-5 w-5')} />
          )}
        </button>
      )}

      {!isReady && (
        <p className={cn(
          'mt-2 text-xs text-center max-w-[240px]',
          voiceError ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
        )}>
          {voiceError
            ? voiceError
            : softphoneState === 'registering'
              ? 'Connecting softphone...'
              : softphoneState === 'error'
                ? 'Softphone error — check settings'
                : !enableVoiceCalling
                  ? 'Voice calling is disabled. Enable it in Settings.'
                  : !isAvailable
                    ? 'Turn on availability to make and receive calls.'
                    : 'Softphone not connected'}
        </p>
      )}
    </div>
  );
}

// ── CallerIdPicker ──────────────────────────────────────────────────

import type { CallerID } from '@/types/voice';

/** `compact` = inline in a call bar (CRM call manager): no label, fixed width. */
export function CallerIdPicker({
  callerIds,
  selectedCallerId,
  onSelect,
  compact,
}: {
  callerIds: CallerID[];
  selectedCallerId: string | null;
  onSelect: (value: string) => void;
  compact?: boolean;
}) {
  const preferences = useSettingsStore((s) => s.preferences);
  const updatePreferences = useSettingsStore((s) => s.updatePreferences);

  const defaultCallerId = preferences.defaultCallerId;
  // The user's OWN hide list. Admin-hidden numbers never reach this picker:
  // `callerIds` comes from useVisibleCallerIds (KAN-178).
  const hiddenCallerIds: string[] = preferences.hiddenCallerIds || [];

  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Visible = not hidden by the user's own preference
  const visibleCallerIds = callerIds.filter((c) => !hiddenCallerIds.includes(c.value));
  const userHasHidden = hiddenCallerIds.length > 0 && hiddenCallerIds.some((h) => callerIds.some((c) => c.value === h));
  const [showHidden, setShowHidden] = useState(false);

  if (callerIds.length === 0) return null;

  const selectedObj = visibleCallerIds.find((c) => c.value === selectedCallerId)
    || callerIds.find((c) => c.value === selectedCallerId);
  const isDefault = defaultCallerId === selectedCallerId;

  const toggleHide = (value: string) => {
    const current = preferences.hiddenCallerIds || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updatePreferences({ hiddenCallerIds: updated });
  };

  const toggleDefault = (value: string) => {
    updatePreferences({
      defaultCallerId: value === defaultCallerId ? undefined : value,
    });
    if (value !== defaultCallerId) {
      onSelect(value);
    }
  };

  return (
    <div className={compact ? 'w-[220px] min-w-[120px] max-w-[45%] shrink' : 'w-full mb-3'}>
      {!compact && (
        <label className="block text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">
          Caller ID
        </label>
      )}

      {/* Custom dropdown */}
      <div className="relative">
        <button
          onClick={() => setDropdownOpen((v) => !v)}
          className="w-full flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm text-foreground hover:bg-muted/50 transition-colors text-left"
        >
          <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="flex-1 truncate">
            {selectedObj
              ? (selectedObj.channel === 'WHATSAPP' ? `WhatsApp: ${selectedObj.label}` : selectedObj.label)
              : 'Select caller ID'}
          </span>
          {isDefault && (
            <Star className="h-3 w-3 text-amber-500 fill-amber-500 shrink-0" />
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </button>

        {dropdownOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
            <div className={cn(
              'absolute top-full left-0 mt-1 z-50 rounded-lg border border-border bg-card shadow-lg max-h-[240px] overflow-y-auto',
              compact ? 'w-[300px] max-w-[calc(100vw-24px)]' : 'right-0'
            )}>
              {/* Visible caller IDs */}
              {visibleCallerIds.map((cid) => (
                <CallerIdRow
                  key={cid.id}
                  cid={cid}
                  isSelected={cid.value === selectedCallerId}
                  isDefault={cid.value === defaultCallerId}
                  isHidden={false}
                  onSelect={() => { onSelect(cid.value); setDropdownOpen(false); }}
                  onToggleDefault={() => toggleDefault(cid.value)}
                  onToggleHide={() => toggleHide(cid.value)}
                />
              ))}

              {/* Show hidden toggle */}
              {userHasHidden && (
                <button
                  onClick={() => setShowHidden((v) => !v)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-muted-foreground hover:bg-muted/30 transition-colors border-t border-border/50"
                >
                  {showHidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  {showHidden ? 'Hide hidden numbers' : `Show ${hiddenCallerIds.filter((h) => callerIds.some((c) => c.value === h)).length} hidden`}
                </button>
              )}

              {/* Hidden caller IDs */}
              {showHidden && callerIds
                .filter((c) => hiddenCallerIds.includes(c.value))
                .map((cid) => (
                  <CallerIdRow
                    key={cid.id}
                    cid={cid}
                    isSelected={cid.value === selectedCallerId}
                    isDefault={cid.value === defaultCallerId}
                    isHidden={true}
                    onSelect={() => { onSelect(cid.value); setDropdownOpen(false); }}
                    onToggleDefault={() => toggleDefault(cid.value)}
                    onToggleHide={() => toggleHide(cid.value)}
                  />
                ))
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Single row in the caller ID dropdown */
function CallerIdRow({
  cid,
  isSelected,
  isDefault,
  isHidden,
  onSelect,
  onToggleDefault,
  onToggleHide,
}: {
  cid: CallerID;
  isSelected: boolean;
  isDefault: boolean;
  isHidden: boolean;
  onSelect: () => void;
  onToggleDefault: () => void;
  onToggleHide: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 px-2 py-1.5 hover:bg-muted/50 transition-colors group',
        isSelected && 'bg-primary/5',
        isHidden && 'opacity-50'
      )}
    >
      {/* Select button — main click area */}
      <button
        onClick={onSelect}
        className="flex-1 flex items-center gap-2 text-xs text-left min-w-0"
      >
        <Phone className={cn('h-3 w-3 shrink-0', isSelected ? 'text-primary' : 'text-muted-foreground')} />
        <span className={cn('truncate', isSelected && 'text-primary font-medium')}>
          {cid.channel === 'WHATSAPP' ? `WhatsApp: ${cid.label}` : cid.label}
        </span>
      </button>

      {/* Action icons */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Set as default */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleDefault(); }}
          className={cn(
            'rounded p-1 transition-colors',
            isDefault
              ? 'text-amber-500'
              : 'text-muted-foreground/30 hover:text-amber-500 opacity-0 group-hover:opacity-100'
          )}
          title={isDefault ? 'Remove as default' : 'Set as default'}
        >
          <Star className={cn('h-3 w-3', isDefault && 'fill-amber-500')} />
        </button>

        {/* Hide / Show */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleHide(); }}
          className={cn(
            'rounded p-1 transition-colors',
            isHidden
              ? 'text-muted-foreground hover:text-foreground'
              : 'text-muted-foreground/30 hover:text-muted-foreground opacity-0 group-hover:opacity-100'
          )}
          title={isHidden ? 'Show this number' : 'Hide this number'}
        >
          {isHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
        </button>
      </div>
    </div>
  );
}
